import type { LayerSpecification, Map as MapLibreMap } from 'maplibre-gl'
import type { Journey } from '@/types/journey'

/**
 * The selected journey, drawn read-only.
 *
 * Read-only is the whole design: the editor is MapTap's, LiveView's proxy is
 * GET-only, and nothing here listens for a click. What it has to answer is
 * "is this the walk I meant?" — so the current floor is solid, the other floors
 * are dimmed rather than hidden (a journey is multi-floor by nature, and hiding
 * the other half of it would make a two-floor tour look like half a tour), and
 * the floor-change legs are dashed because nobody walks a lift shaft.
 */

export const JOURNEY_LINE_SOURCE = 'liveview-journey-line'
export const JOURNEY_POINT_SOURCE = 'liveview-journey-points'

export const JOURNEY_LINE_LAYER = 'liveview-journey-line'
export const JOURNEY_TRANSITION_LAYER = 'liveview-journey-transition'
export const JOURNEY_POINT_LAYER = 'liveview-journey-point'
export const JOURNEY_LABEL_LAYER = 'liveview-journey-label'

export const JOURNEY_LAYERS = [
  JOURNEY_LINE_LAYER,
  JOURNEY_TRANSITION_LAYER,
  JOURNEY_POINT_LAYER,
  JOURNEY_LABEL_LAYER,
]

const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

const onLevel = (level: number, current: number | null) =>
  current === null || Math.abs(level - current) < 1e-9

/**
 * One LineString per leg, so each can say which floor it is on and whether it
 * is a floor change — a single line for the whole journey could say neither.
 */
export function buildJourneyLine(
  journey: Journey | null,
  level: number | null,
): GeoJSON.FeatureCollection {
  if (!journey?.waypoints?.length) return EMPTY
  const features: GeoJSON.Feature[] = []

  for (let i = 0; i < journey.waypoints.length - 1; i++) {
    const here = journey.waypoints[i]
    const next = journey.waypoints[i + 1]
    features.push({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: [
          [here.lng, here.lat],
          [next.lng, next.lat],
        ],
      },
      properties: {
        index: i,
        // A floor change belongs to the floor it starts on, like the player's
        // own reported level: the dot does not appear upstairs until it arrives.
        here: onLevel(here.level, level),
        levelChange: here.level !== next.level,
      },
    })
  }

  return { type: 'FeatureCollection', features }
}

export function buildJourneyPoints(
  journey: Journey | null,
  level: number | null,
): GeoJSON.FeatureCollection {
  if (!journey?.waypoints?.length) return EMPTY
  const last = journey.waypoints.length - 1

  return {
    type: 'FeatureCollection',
    features: journey.waypoints.map((waypoint, index) => {
      const dwell = typeof waypoint.dwell_s === 'number' && waypoint.dwell_s > 0
      const label =
        waypoint.label ?? (index === 0 ? 'Start' : index === last ? 'End' : `${index + 1}`)
      return {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [waypoint.lng, waypoint.lat] },
        properties: {
          index,
          label: dwell ? `${label} · ${Math.round(waypoint.dwell_s as number)} s` : label,
          here: onLevel(waypoint.level, level),
          dwell,
          terminal: index === 0 || index === last,
        },
      }
    }),
  }
}

/** Bounds covering every waypoint, for framing the journey when it is picked. */
export function journeyBounds(journey: Journey | null): [number, number, number, number] | null {
  const waypoints = journey?.waypoints ?? []
  if (waypoints.length === 0) return null
  let west = Infinity
  let south = Infinity
  let east = -Infinity
  let north = -Infinity
  for (const waypoint of waypoints) {
    west = Math.min(west, waypoint.lng)
    east = Math.max(east, waypoint.lng)
    south = Math.min(south, waypoint.lat)
    north = Math.max(north, waypoint.lat)
  }
  return Number.isFinite(west) ? [west, south, east, north] : null
}

const layerDefinitions = (): LayerSpecification[] => [
  {
    id: JOURNEY_LINE_LAYER,
    type: 'line',
    source: JOURNEY_LINE_SOURCE,
    filter: ['!', ['get', 'levelChange']],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      // The journey hue band, so a drawn journey reads as the same kind of
      // thing as the dot that will walk it.
      'line-color': '#c084fc',
      'line-width': ['interpolate', ['linear'], ['zoom'], 14, 2, 20, 5],
      'line-opacity': ['case', ['get', 'here'], 0.95, 0.22],
    },
  },
  {
    // Nobody walks a lift shaft, and a solid line between two floors reads as
    // if somebody did.
    id: JOURNEY_TRANSITION_LAYER,
    type: 'line',
    source: JOURNEY_LINE_SOURCE,
    filter: ['get', 'levelChange'],
    layout: { 'line-cap': 'round' },
    paint: {
      'line-color': '#c084fc',
      'line-width': ['interpolate', ['linear'], ['zoom'], 14, 2, 20, 5],
      'line-opacity': ['case', ['get', 'here'], 0.9, 0.22],
      'line-dasharray': [1.5, 1.5],
    },
  },
  {
    id: JOURNEY_POINT_LAYER,
    type: 'circle',
    source: JOURNEY_POINT_SOURCE,
    paint: {
      'circle-color': ['case', ['get', 'dwell'], '#f0abfc', '#c084fc'],
      'circle-radius': [
        'interpolate',
        ['linear'],
        ['zoom'],
        14,
        ['case', ['get', 'terminal'], 5, 3.5],
        20,
        ['case', ['get', 'terminal'], 9, 6],
      ],
      'circle-opacity': ['case', ['get', 'here'], 0.95, 0.25],
      'circle-stroke-width': 1.5,
      'circle-stroke-color': 'rgba(255,255,255,0.85)',
      'circle-stroke-opacity': ['case', ['get', 'here'], 0.9, 0.2],
    },
  },
  {
    id: JOURNEY_LABEL_LAYER,
    type: 'symbol',
    source: JOURNEY_POINT_SOURCE,
    minzoom: 17,
    // Labels for the floor being looked at only: the dimmed geometry is there
    // for context, and its labels would sit on top of the live ones.
    filter: ['get', 'here'],
    layout: {
      'text-field': ['get', 'label'],
      // A single-font stack on purpose: api.proximi.fi serves `Open Sans Bold`
      // but not the two-font `…,Arial Unicode MS Bold` combination, and a
      // fontstack the glyph server lacks makes every label vanish silently.
      'text-font': ['Open Sans Bold'],
      'text-size': 12,
      'text-offset': [0, -1.2],
      'text-anchor': 'bottom',
      'text-allow-overlap': false,
      'text-optional': true,
    },
    paint: {
      // Dark text on an opaque white halo. The previous pale text on a
      // translucent dark halo let the basemap bleed through the halo, so the
      // edge of the glyphs and the halo blurred into one grey shape.
      'text-color': '#3b0764',
      'text-halo-color': '#ffffff',
      'text-halo-width': 1.6,
      'text-halo-blur': 0,
    },
  },
]

/**
 * Installs the journey layers. Called after every `setStyle`, like the live
 * ones — MapLibre drops non-style sources and layers when the style is replaced.
 */
export function addJourneyLayers(map: MapLibreMap): void {
  for (const layer of layerDefinitions()) {
    if (map.getLayer(layer.id)) map.removeLayer(layer.id)
    const sourceId = 'source' in layer ? (layer.source as string) : undefined
    if (!sourceId || !map.getSource(sourceId)) continue
    map.addLayer(layer)
  }
}
